import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../sidebar/Sidebar';
import { useSelectedStudent } from '../../context/SelectedStudentContext';
import { useOverviewContext } from '../../context/OverviewContext';
import { useSchool } from '@context/SchoolContext';
import StudentSwitcherModal from '../../components/StudentSwitcherModal/StudentSwitcherModal';
import { getLogo } from '../../utils/globalUtils';
import k12Logo from '../../assets/images/k12_logo.svg';
import { MobileHamburgerIcon, SwitchStudentIcon } from '@/components/MenusIcons/MenusIcons';
import { useSkeletonLoader } from '@/hooks/useSkeletonLoader/useSkeletonLoader';
import { LogoSkeleton, SwitchSkeleton, HamburgerSkeleton } from '@components/Skeletons/Skeletons';

export type MenuType = 'multi-student-lc' | 'single-student-lc' | 'student';
interface HeaderProp {
  type: MenuType;
  showSwitchButton: boolean;
}

const TopNavMobile: React.FC<HeaderProp> = ({
  type,
  showSwitchButton
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const [showStudentModal, setShowStudentModal] = useState(false);

  const { students, userRoleVal } = useOverviewContext();
  const { selectedStudent } = useSelectedStudent();
  const fetchStudents = async () => students ?? [];
  const hasMultipleStudents = students && students.length > 1;
  
  const studentName = selectedStudent
  ? `${selectedStudent.firstName} ${selectedStudent.lastName}`
  : '';
  const schoolShortName = selectedStudent?.schoolShortName || '';
  const grade = selectedStudent?.gradeId ? `Grade ${selectedStudent.gradeId}` : '';
  const gradeTagline = [schoolShortName, grade].filter(Boolean).join(' | ');

  const isTablet = window.innerWidth >= 800; //TODO refer the variable from the variable.
  const directionClass = isTablet ? 'from-right' : 'from-left';
  const headerRef = useRef<HTMLElement>(null);

  // Skeleton
  const { data: schoolData, error: schoolError } = useSchool();
  const showSkeleton = useSkeletonLoader({
    schoolData,
    schoolError,
    selectedStudent,
    retryDelay: 3000,
    initialDelay: 2000,
    maxRetries: 2,
  });

  // Keep subtask sticky headers offset in sync with the measured mobile nav height.
  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return;

    const layoutBodyEl = headerEl.closest('.layout-body');
    const layoutBody = layoutBodyEl instanceof HTMLElement ? layoutBodyEl : null;
    const mobileQuery = window.matchMedia('(max-width: 1099.98px)');

    const syncStickyOffset = () => {
      if (!mobileQuery.matches) {
        layoutBody?.style.removeProperty('--tasklist-subtask-sticky-top');
        return;
      }
      const height = Math.ceil(headerEl.getBoundingClientRect().height);
      layoutBody?.style.setProperty('--tasklist-subtask-sticky-top', `${height}px`);
    };

    syncStickyOffset();
    const observer = new ResizeObserver(syncStickyOffset);
    observer.observe(headerEl);
    mobileQuery.addEventListener('change', syncStickyOffset);

    return () => {
      observer.disconnect();
      mobileQuery.removeEventListener('change', syncStickyOffset);
      layoutBody?.style.removeProperty('--tasklist-subtask-sticky-top');
    };
  }, [showSkeleton, studentName, gradeTagline, hasMultipleStudents]);
  
  // Handle menu
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [menuOpen]);


  // Get the Logo WP API
  const [logoSrc, setLogoUrl] = useState<string>('');
  useEffect(() => {
    const timeout = setTimeout(() => {
      const logoSrc = getLogo();
      if (logoSrc) {
        setLogoUrl(logoSrc);
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <header ref={headerRef} className="header mobile-view">
      <div className="container-fluid header mobile-view d-xl-none">
        <div className="mobileTopNav seconday-menu">
          
          <div className="mobile-header">
            <div className="header-left">
              <a className="text-center" id="logo-div" href="/">
              {showSkeleton ? (
                <LogoSkeleton />
              ) : (
                <img src={logoSrc || k12Logo} alt="K12 Logo" className="k12Logo" />
              )}
                </a>
            </div>

            {showSkeleton ? (
                <SwitchSkeleton />
                  ) : (  
                    (type === 'multi-student-lc' && showSwitchButton && hasMultipleStudents) ? (
                      <div className="studentDetailsWrapper" onClick={() => setShowStudentModal(true)}>
                        <div className="studentInfo">
                          <span className="studentName">{studentName}</span>
                          <span className="gradeTagline">{gradeTagline}</span>
                        </div>
                        <SwitchStudentIcon />
                      </div>
                    ) : (
                      <div className="studentDetailsWrapper">
                        <div className="studentInfo">
                          <span className="studentName">{studentName}</span>
                          <span className="gradeTagline">{gradeTagline}</span>
                        </div>
                      </div>
                    )
                  )}  
            <div className="header-right">
              {showSkeleton ? (
                <HamburgerSkeleton />
                  ) : (    
                    <div className="hamburgerMenu" aria-label="Main Menu" onClick={() => setMenuOpen(true)}>
                      <MobileHamburgerIcon />
                    </div>
                  )
              }
            </div>
          </div>
            <>
              <div className={`mobileScrim ${menuOpen ? 'visible' : ''}`} onClick={() => setMenuOpen(false)} />
              <div className={`mobileSidebarDrawer  ${directionClass} ${menuOpen ? 'open' : ''}`}>
                <Sidebar 
                  onItemClick={closeMenu}
                  onSwitchStudent={() => setShowStudentModal(true)}
                />
              </div>
            </>
          
        </div>
      </div>

      {showStudentModal && (
        <StudentSwitcherModal
          variant={userRoleVal === 'TEACHER' ? 'school' : 'student'}
          fetchStudents={fetchStudents}
          onClose={() => setShowStudentModal(false)}
          onStudentSelect={() => setShowStudentModal(false)}
        />
      )}
      
    </header>
  );
};

export default TopNavMobile;
